(ns ncr.fragment-types.pdf-viewer
  (:require
   [ncr.etc.pf :as pf]
   [ncr.widgets.core :as w]
   [ncr.widgets.pf :as wpf]
   [uix.core.alpha :as uix]
   ["pdfjs-dist/build/pdf" :as pdfjs]
   ["pdfjs-dist/web/pdf_viewer" :refer [EventBus LinkTarget PDFLinkService PDFViewer]]
   [ncr.fragment.attachment-markup :as am]
   [ncr.fragment-types.widgets :as ftw]
   [ncr.fragment.ai-assist :as assist]))

(defn- c-toolbar [fragment-name attachment close zoom-out zoom-in current-zoom zoom attachments set-preview]
  [ftw/c-toolbar fragment-name attachment close attachments set-preview

   [:<>

    [pf/c-toolbar-group
     {:variant :icon-button-group}

     [w/c-toolbar-button
      {:icon :magnify-minus-outline
       :on-click zoom-out
       :tooltip "Zoom Out"}]

     [w/c-toolbar-button
      {:icon :magnify-plus-outline
       :on-click zoom-in
       :tooltip "Zoom In"}]

     (let
       [options
        [{:id :0.5 :label "50%"}
         {:id :0.75 :label "75%"}
         {:id :1 :label "100%"}
         {:id :1.25 :label "125%"}
         {:id :1.50 :label "150%"}
         {:id :2 :label "200%"}
         {:id :3 :label "300%"}
         {:id :4 :label "400%"}
         {:id :auto :label "Automatic Zoom"}
         {:id :actual :label "Actual Size"}
         {:id :page-fit :label "Page Fit"}
         {:id :page-width :label "Page Width"}]
        selected (some #(when (= current-zoom (:id %)) %) options)
        placeholder-text
        (if selected
          (:name selected)
          (-> current-zoom (* 100) .toFixed (str "%")))]
       [wpf/c-pf-select
        {:variant :single
         :placeholder-text placeholder-text
         :selections selected
         :on-select
         (fn [_ {:keys [id]} _]
           (zoom (keyword id)))}
        options])]

    [pf/c-toolbar-group
     {:variant :icon-button-group}
     [am/toolbar-icon]
     (let [{:keys [auxdata reference-id]} attachment]
       [assist/toolbar-button {:reference reference-id :data (:assist auxdata)}])]]])


(def ^:private zoom-pref-key
  "neckar.pdf-viewer.zoom")

(defn- read-zoom-pref []
  (let [zoom (or (js/localStorage.getItem zoom-pref-key) "1")]
    (if (re-find #"[0-9.]+" zoom)
      zoom
      (keyword zoom))))

(defn- write-zoom-pref [zoom]
  (js/localStorage.setItem zoom-pref-key (name zoom)))

(defn c-viewer [fragment-name attachment url close attachments set-preview]
  (let
    [current-zoom (uix/state (read-zoom-pref))
     error-msg (uix/state nil)
     document-loading? (uix/state false)
     visible-pages (uix/state nil)
     ^js event-bus (uix/memo (fn [] (EventBus.)) [])
     canvas (uix/state nil)
     canvas-ref (uix/callback #(some->> % (reset! canvas)) [])
     ^js viewer (uix/memo
                  (fn []
                    (when-let [canvas @canvas]
                      (let [^js link-service (PDFLinkService.
                                               (clj->js {:eventBus event-bus
                                                         :externalLinkTarget (.-BLANK LinkTarget)}))
                            pdf-viewer (PDFViewer.
                                         (clj->js {:container canvas
                                                   :linkService link-service
                                                   :eventBus event-bus}))]
                        (.setViewer link-service pdf-viewer)
                        pdf-viewer)))
                  [@canvas])

     zoom
     (fn [val]
       (write-zoom-pref
         (if (#{:in :out} val)
           (do
             (if (= :in val)
               (.increaseScale viewer)
               (.decreaseScale viewer))
             (let [new-zoom (.-currentScale viewer)]
               (reset! current-zoom new-zoom)
               (-> new-zoom str keyword)))
           (do
             (set! (.-currentScaleValue viewer)
               (name
                 (if (= :actual val) "1" val)))
             (reset! current-zoom val)
             val))))

     get-document
     (fn []
       (reset! document-loading? true)
       (-> pdfjs
         (.getDocument #js {:url url})
         (.-promise)
         #_(.then
             (fn [^js pdf]
               (reset! document-loading? false)
               (if (pos? (js/Math.floor (* (js/Math.random) 2)))
                 (throw (js/Error. "Artificial error for testing purposes"))
                 pdf)))
         (.then
           (fn [^js pdf]
             (reset! error-msg nil)
             (reset! document-loading? false)
             (.setDocument viewer pdf))
           (fn [^js e]
             (reset! error-msg e.message)
             (reset! document-loading? false)))))

     [set-attachment] (.get am/useStore [:set-attachment])

     update-visible-pages
     (fn [& _]
       (reset! visible-pages
         (map
           (fn [^js item]
             {:id (.-id item) :x (.-x item) :y (.-y item) :viewport (.. item -view -viewport)})
           (.-views (._getVisiblePages ^js viewer)))))]

    (uix/effect!
      (fn []
        (set-attachment attachment))
      [attachment])

    (uix/effect!
      (fn []
        (when viewer (get-document)))
      [viewer url])


    (uix/effect!
      (fn []
        (when viewer

          (.on event-bus "pagesinit"
            (fn [_]
              (zoom @current-zoom)
              (when-let [^js canvas @canvas]
                (set! (.-scrollTop canvas) 0))))

          (.on event-bus "updateviewarea" update-visible-pages)

          (fn []
            (when viewer (.cleanup viewer)))))
      [viewer])

    (uix/effect!
      (fn []
        (when-let [canvas @canvas]
          (let
            [container-resize-observer
             (new js/ResizeObserver
               (fn [_]
                 (if (= :page-width @current-zoom)
                   (zoom :page-width)
                   (update-visible-pages))))]
            (.observe container-resize-observer canvas)
            (fn []
              (.disconnect container-resize-observer)))))
      [@canvas])

    [:<>
     [c-toolbar fragment-name attachment close
      #(zoom :out) #(zoom :in) @current-zoom zoom
      attachments
      (fn [id mime-type]
        (set-preview id mime-type
          (when (= "application/pdf" mime-type) #(zoom :page-width))))]

     [ftw/c-content @error-msg @document-loading? get-document
      [:<>
       [:div.ncr-c-pdf-viewer {:ref canvas-ref}
        [:div.pdfViewer]
        [am/markup-viewer {:attachments attachments :pages @visible-pages}]]]]]))
